import { Box, Button, CircularProgress, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'

import VideoPlayerCloseConfirmationDialog from '../../alive-academics/components/student-hybrid-class/VideoPlayerCloseConfirmationDialog'
import AccessExtensionRequestDialog from '../../alive-academics/components/student-hybrid-class/AccessExtensionRequestDialog'
import HybridClassVideoPlayerDialog from '../../alive-academics/components/student-hybrid-class/HybridClassVideoPlayerDialog'
import { MAX_EXTENSION_REASON_LENGTH, VIDEO_PROGRESS_UPDATE_INTERVALS } from '../../shared/utils/constants'
import StudentQuizDialog from '../../alive-academics/components/student-hybrid-class/StudentQuizDialog'
import HybridClassVideo from '../../alive-academics/components/student-hybrid-class/HybridClassVideo'
import TryAgainMessageBlock from '../../components/common/TryAgainMessageBlock'
import { formatDate, Icons, leadingZeroFormatter } from '../../utils/utilities'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import { getTransformedMeetingType } from '../../utils/meeting-utilities'
import { useOnlineSessionsStore } from '../../store/onlinesessions.store'
import { useQuizAndQuestionbankStore } from '../../store/quiz.store'
import BreadcrumbList from '../../components/common/BreadcrumbList'
import { useSettingsStore } from '../../store/settings.store'
import CustomChip from '../../components/common/CustomChip'
import noRecordingsImg from '../../assets/norecording.svg'
import { useUserStore } from '../../store/user.store'
import api from '../../service/api'

const HybridClassDetailsForStudent = observer( () => {

    const [videoPlayerCloseConfirmationDialogOpen, setVideoPlayerCloseConfirmationDialogOpen] = useState( false )
    const [requestInitializerWindowOpen, setRequestInitializerWindowOpen] = useState( false )
    const [watchingVideoDetails, setWatchingVideoDetails] = useState( null )
    const [timeExtensionRequest, setTimeExtensionRequest] = useState( null )
    const [requestingExtension, setRequestingExtension] = useState( false )
    const [upNextBadgeDetails, setUpNextBadgeDetails] = useState( false )
    const [progressSyncFailing, setProgressSyncFailing] = useState( false )
    const [fetchingVideoLink, setFetchingVideoLink] = useState( false )
    const [eventPopupDetails, setEventPopupDetails] = useState( null )
    const [loadingRecordings, setLoadingRecordings] = useState( true )
    const [meetingRecordings, setMeetingRecordings] = useState( [] )
    const [videosFetchError, setVideosFetchError] = useState( null )
    const [videoProgresses, setVideoProgresses] = useState( {} )
    const [recordingLink, setRecordingLink] = useState( null )
    const [videoDefaults, setVideoDefaults] = useState( {} )

    const { palette, border, layout } = useTheme()
    const { meeting_id } = useParams()
    const { pathname } = useLocation()

    const [searchParams, setSearchParams] = useSearchParams( { quiz_id: "", class_id: "", instance_id: "" } )
    const selectQuizInstanceId = searchParams.get( 'instance_id' ) || ""
    const selectQuizClassId = searchParams.get( 'class_id' ) || ""
    const selectQuizId = searchParams.get( 'quiz_id' ) || ""

    const OnlineSessionsStore = useOnlineSessionsStore()
    const QuizStore = useQuizAndQuestionbankStore()
    const SettingsStore = useSettingsStore()
    const UserStore = useUserStore()

    const navigate = useNavigate()

    const progressRequestInProgress = useRef( false )
    const videoProgressLastUpdatedAt = useRef()
    const lastSavedVideoProgress = useRef( {} )
    const progressSyncFailureCount = useRef( 0 )

    const meetingFetchError = OnlineSessionsStore.getErrors.onlineSession
    const meeting = OnlineSessionsStore.getSessionMetadata

    const { initialLoad: loading, refreshing } = OnlineSessionsStore.getMetadataLoadingStatus

    const maxExtendableTime = SettingsStore.getUseSettings?.["hybrid_class_max_video_extend_time"] || '22:00'
    const requestsEnabled = SettingsStore.getUseSettings?.["hybrid_class_enable_access_time_extension_requests"] || false

    const maxExtendTimeDetails = useMemo( () => {
        const hourAndMinute = maxExtendableTime.split( ":" )
        const [hour, minute] = hourAndMinute.map( i => +i )
        if ( hour && Number.isInteger( hour ) && hour > 0 && hour <= 24 && Number.isInteger( minute ) && minute >= 0 && minute <= 60 ) {
            return { hour24: leadingZeroFormatter.format( hour ), minute, hour12: leadingZeroFormatter.format( ( hour % 12 ) || 12 ) }
        } else {
            return { hour24: "17", minute: "20", hour12: "05" }
        }
    }, [maxExtendableTime] )

    const canRequestAccessTimeExtension = useMemo( () => {

        if ( !meeting || !maxExtendTimeDetails || !requestsEnabled ) return false

        const earliestAccessEndTime = dayjs( Math.min( ...meetingRecordings.map( v => new Date( v.access_ends_at ).getTime() ) ) )
        const lastAccessTimePassed = dayjs( earliestAccessEndTime ).isBefore( dayjs() )
        const notExtendedAlready = dayjs( earliestAccessEndTime ).isBefore( dayjs( meeting.ends_at ).set( 'hour', +maxExtendTimeDetails.hour24 ).set( 'minute', +maxExtendTimeDetails.minute ) )
        const firstDay = dayjs( meeting.starts_at ).isSame( dayjs(), 'day' )

        return firstDay && notExtendedAlready && lastAccessTimePassed

    }, [meetingRecordings, maxExtendTimeDetails, meeting, requestsEnabled] )

    const playVideo = async ( url, video ) => {
        try {
            let key, video_id
            let expired = true

            if ( video.type === "recording" ) {
                const mp4File = video.files_list.find( re => re.filename?.endsWith( "mp4" ) )
                key = mp4File.key
            } else {
                key = video.file_key
                video_id = video.id
                expired = dayjs( video.endDate ).isBefore( dayjs() )
                if ( expired || video.expired ) {
                    if ( dayjs( video.endDate ).isSame( dayjs(), 'day' ) )
                        return toast( "Video access expired! Please request time extention" )

                }
                if ( !meeting.started )
                    return toast( "Please wait for the class to start." )
            }

            setFetchingVideoLink( true )
            const watchRegistrationNeeded = video.type !== "recording" && !expired
            // const { data: { data } } = await api.recording.fetchMeetingRecordingSignedUrl( meeting_id, { key: key, register_watch: watchRegistrationNeeded, video_id, class_id: meeting_id } )
            if ( videoProgresses[video.id] !== 100 )
                videoProgressLastUpdatedAt.current = dayjs()

            progressSyncFailureCount.current = 0
            setProgressSyncFailing( false )

            const defaults = {}
            if ( videoProgresses[video.id] )
                defaults.start_percent = videoProgresses[video.id]

            setWatchingVideoDetails( video )
            setUpNextBadgeDetails( null )
            setVideoDefaults( defaults )
            setRecordingLink( url )
        } catch ( err ) {
            console.log( err )
        } finally {
            setFetchingVideoLink( false )
        }
    }

    const showNextResource = () => {
        if ( upNextBadgeDetails ) return

        const classVideos = meetingRecordings.filter( v => v.type === "class-video" )
        if ( classVideos.length > 0 ) {
            const currentIndex = classVideos.findIndex( v => v.id === watchingVideoDetails.id )
            if ( currentIndex + 1 < classVideos.length ) {
                setUpNextBadgeDetails( { type: "video", video: classVideos[currentIndex + 1] } )
            } else {
                setUpNextBadgeDetails( { type: "quiz" } )
            }
        } else {
            setUpNextBadgeDetails( { type: "quiz" } )
        }
    }

    const closeVideoPlayer = () => {
        setRecordingLink( null )
        setWatchingVideoDetails( null )
        setVideoDefaults( {} )
        getVideoWatchProgress()
        setVideoPlayerCloseConfirmationDialogOpen( false )
    }

    const requestAccessTimeExtension = async ( reason ) => {
        const trimmedReason = reason?.trim() || ""
        if ( !trimmedReason || trimmedReason.length > MAX_EXTENSION_REASON_LENGTH )
            return false

        try {
            setRequestingExtension( true )
            const { data: { data: request } } = await api.onlineMeetings.requestHybridClassAccessTimeExtension( meeting_id, { reason: trimmedReason } )
            const updatedRecordings = await fetchMeetingRecordings()

            if ( watchingVideoDetails ) {
                const updatedVideo = updatedRecordings?.find( v => v.id === watchingVideoDetails.id )
                if ( updatedVideo )
                    setWatchingVideoDetails( updatedVideo )
            }

            setTimeExtensionRequest( request )
            setRequestInitializerWindowOpen( false )
            return true
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Failed to send the time extension request. Please try again." )
            return false
        } finally {
            setRequestingExtension( false )
        }
    }

    const handleVideoPlayerTimeUpdate = async ( event ) => {

        if ( progressRequestInProgress.current ) return


        const video = event.target

        try {
            progressRequestInProgress.current = true
            const expired = dayjs( watchingVideoDetails.endDate ).isBefore( dayjs() )
            if ( !expired ) {
                const { duration, currentTime } = event.target
                const progress = Math.ceil( ( currentTime * 100 ) / duration )

                if ( progress > 90 ) {
                    showNextResource()
                } else if ( upNextBadgeDetails ) {
                    setUpNextBadgeDetails( null )
                }

                if ( !lastSavedVideoProgress.current[watchingVideoDetails.id] || ( lastSavedVideoProgress.current[watchingVideoDetails.id] < progress ) ) {

                    if ( videoProgressLastUpdatedAt.current && dayjs().diff( videoProgressLastUpdatedAt.current ) > VIDEO_PROGRESS_UPDATE_INTERVALS ) {
                        // Advance the throttle timestamp before the call resolves so a failed update is
                        // retried at the normal interval instead of on every timeupdate tick.
                        videoProgressLastUpdatedAt.current = dayjs()
                        await api.onlineMeetings.updateHybridClassVideoWatchProgress( meeting_id, watchingVideoDetails.id, { progress, legacy: watchingVideoDetails.legacy } )
                        lastSavedVideoProgress.current[watchingVideoDetails.id] = progress
                        progressSyncFailureCount.current = 0
                        setProgressSyncFailing( false )
                    }
                }
            } else {
                const { duration, currentTime } = event.target
                const progress = Math.ceil( ( currentTime * 100 ) / duration )
                videoProgressLastUpdatedAt.current = null
            }
        } catch ( err ) {
            if ( err?.response?.data?.errorCode === "PROGRESS_SPEEDRUN_ERROR" ) {
                toast( `Please watch the video completely without skipping any part!` )
                try {
                    await video.pause()
                    video.currentTime = video.duration * ( lastSavedVideoProgress.current[watchingVideoDetails.id] / 100 )
                } catch ( err ) {

                }
            } else if ( err?.response?.status === 401 || err?.response?.data?.unauth ) {
                await closeVideoPlayer()
                UserStore.setERPSessionState( "ended" )
            } else {
                progressSyncFailureCount.current += 1
                if ( progressSyncFailureCount.current >= 3 ) {
                    setProgressSyncFailing( true )
                } else {
                    toast( err?.response?.data?.message || `Failed to register watch progress!`, { autoClose: 2000 } )
                }
            }
            console.log( err )
        } finally {
            progressRequestInProgress.current = false
        }
    }

    const handleVideoCompleted = async ( event ) => {
        const video = event.target
        try {
            if ( watchingVideoDetails ) {
                const expired = dayjs( watchingVideoDetails.endDate ).isBefore( dayjs() )

                if ( !expired ) {
                    await api.onlineMeetings.updateHybridClassVideoWatchProgress( meeting_id, watchingVideoDetails.id, { progress: 100 } )
                    videoProgressLastUpdatedAt.current = null
                } else {
                    videoProgressLastUpdatedAt.current = null
                }
                if ( watchingVideoDetails.id === meetingRecordings.at( -1 ).id ) {
                    const quiz = QuizStore.getClassQuizzes[meeting.time_table_id]?.at( 0 )
                    if ( quiz )
                        attemptQuiz( quiz )
                }
            }
        } catch ( err ) {
            if ( err?.response?.data?.errorCode === "PROGRESS_SPEEDRUN_ERROR" ) {
                toast( `Please watch the video completely without skipping any part!` )
                try {
                    await video.pause()
                    video.currentTime = video.duration * ( lastSavedVideoProgress.current[watchingVideoDetails.id] / 100 )
                } catch ( err ) {

                }
            } else if ( err?.response?.status === 401 || err?.response?.data?.unauth ) {
                await closeVideoPlayer()
                UserStore.setERPSessionState( "ended" )
            } else {
                toast( err?.response?.data?.message || `Failed to register watch progress!`, { autoClose: 2000 } )
            }
            console.log( err )
        }
    }

    const attemptQuiz = ( quiz ) => {
        if ( quiz ) {
            setSearchParams( prev => {
                const newValues = new URLSearchParams( prev )
                newValues.set( "quiz_id", quiz.quiz_id )
                newValues.set( "instance_id", quiz.id )
                newValues.set( "class_id", quiz.class_id )
                return newValues
            }, { replace: true } )
            closeVideoPlayer()
        }
    }

    const handleRateChange = event => {
        event.preventDefault()
        const video = event.target
        if ( video.playbackRate !== 1 ) {
            video.playbackRate = 1
        }

    }

    const handleTimelineEvent = async ( { event, video, markerEle } ) => {
        try {

            if ( +QuizStore.getQuizInstance.attempted !== 0 ) return

            const questions = []
            const activeEvents = []
            for ( const ev of watchingVideoDetails.video_timeline_events.slice( 0, event.eventIndex + 1 ) ) {
                if ( ev.data?.question ) {
                    const answered = QuizStore.getAttemptingAnswers[ev.data.question] ?? null
                    if ( !answered ) {
                        activeEvents.push( ev )
                    }
                } else {
                    const q = QuizStore.getQuizAttemptQuestions[ev.eventIndex]
                    const answered = QuizStore.getAttemptingAnswers[q.id] ?? null
                    if ( !answered ) {
                        activeEvents.push( ev )
                    }
                }
            }

            for ( const ev of activeEvents ) {
                if ( ev.data?.question ) {
                    const question = QuizStore.getQuizAttemptQuestions.find( q => q.id === +ev.data.question )
                    if ( question )
                        questions.push( question )
                } else {
                    const question = QuizStore.getQuizAttemptQuestions[ev.eventIndex]
                    if ( question )
                        questions.push( question )
                }

            }

            if ( questions.length > 0 ) {
                setEventPopupDetails( { anchorEl: markerEle, event, questions, video } )
                setTimeout( async () => {
                    try {
                        await video.pause()
                    } catch ( err ) { }
                }, 0 )
            }
        } catch ( err ) {
            console.log( err )
        }
    }


    const submitStoredQuizAnswers = async () => {
        // setSubmittingAnswers( true )
        const quizInstance = QuizStore.getClassQuizzes[meeting.time_table_id]?.at( 0 )

        if ( +localStorage.getItem( 'current_quiz_id' ) === +quizInstance.id ) {
            const answeredQuestions = Object.keys( QuizStore.getAttemptingAnswers || JSON.parse( localStorage.getItem( 'quiz_local_answer' ) || '{}' ) )

            if ( answeredQuestions.length === QuizStore.getQuizAttemptQuestions.length ) {
                const multipleSelectionQuestions = QuizStore.getQuizAttemptQuestions.filter( q => q.selection_type === 'multiple' )
                let hasAnsweredAll = true
                for ( let i = 0; i < multipleSelectionQuestions.length; i++ ) {
                    const ques = multipleSelectionQuestions[i]
                    const correctAnswer = QuizStore.getAttemptingAnswers[ques.id]?.filter( a => a === true ).length
                    if ( correctAnswer === 0 ) {
                        hasAnsweredAll = false
                        break
                    }
                }
                if ( hasAnsweredAll ) {
                    const attemptInstanceDetails = QuizStore.getQuizInstance

                    await QuizStore.submitQuizAnswers( quizInstance.quiz_id, quizInstance.id, attemptInstanceDetails.attempt_id )
                    const { success, message } = await QuizStore.fetchQuizInstanceForStudent( quizInstance.quiz_id, quizInstance.id )
                } else {
                    toast( "Please answer all the questions" )
                }
            } else {
                toast( "Please answer all the quiz questions" )
            }
        }

        // setSubmittingAnswers( false )
    }

    const closeQuizPopover = async ( event ) => {
        try {
            await eventPopupDetails.video?.play()
            if ( event.eventIndex === watchingVideoDetails.video_timeline_events?.length - 1 ) {
                submitStoredQuizAnswers()
            }
        } catch ( err ) {
            console.log( err )
        }
        setEventPopupDetails( null )
    }

    const fetchMeeting = useCallback( async () => {

        await OnlineSessionsStore.fetchOnlineSessionMetadata( meeting_id )
        await QuizStore.fetchClassAssociatedQuizInstances()

        return true
    }, [meeting_id, QuizStore, OnlineSessionsStore] )

    const getVideoWatchProgress = useCallback( async () => {
        try {
            const { data: { data: videoProgresses } } = await api.onlineMeetings.fetchMeetingVideoProgresses( meeting_id )
            const progresses = {}
            videoProgresses.forEach( vp => progresses[vp.recording_id || vp.video_id] = vp.watch_progress )
            setVideoProgresses( progresses )
            lastSavedVideoProgress.current = progresses
        } catch ( err ) {
            toast( err?.response?.data?.message || "Failed to fetch video watch progresses. Please try again." )
        }
    }, [meeting_id] )

    const getVideoAccessTimeExtensionRequestDetails = useCallback( async () => {
        try {
            const { data: { data: request } } = await api.onlineMeetings.fetchHybridClassAccessTimeExtensionRequests( meeting_id )
            setTimeExtensionRequest( request )
        } catch ( err ) {
            toast( err?.response?.data?.message || "Failed to fetch time extension request details. Please try again." )
        }
    }, [meeting_id] )

    const fetchMeetingRecordings = useCallback( async () => {

        try {
            setLoadingRecordings( true )
            const { data: { data } } = await api.recording.fetchHybridClassRecordings( meeting_id )
            setMeetingRecordings( data )
            setVideosFetchError( null )
            return data
        } catch ( err ) {
            console.log( err )
            setVideosFetchError( { message: err?.response?.data?.message || "Somethingwent wrong while fecthing the recordings.", code: err?.response?.status || 500 } )
            return null
        } finally {
            setLoadingRecordings( false )
        }

    }, [meeting_id] )

    useEffect( () => {
        fetchMeeting()
        getVideoWatchProgress()
        fetchMeetingRecordings()
        getVideoAccessTimeExtensionRequestDetails()
    }, [fetchMeeting, fetchMeetingRecordings, getVideoWatchProgress, getVideoAccessTimeExtensionRequestDetails] )

    const takeClassQuiz = () => attemptQuiz( QuizStore.getClassQuizzes[meeting?.time_table_id]?.at( 0 ) )

    return (
        <Box padding="20px" position="relative" display={pathname.match( /audit|admin/ ) ? "flex" : "block"} flexDirection="column" overflow={pathname.match( /audit|admin/ ) ? "initial" : "auto"} flexGrow={1}>

            <StudentQuizDialog open={Boolean( selectQuizId && selectQuizClassId && selectQuizInstanceId )} />

            <HybridClassVideoPlayerDialog
                src={recordingLink}
                video={watchingVideoDetails}
                videoDefaults={videoDefaults}
                videoCompleted={watchingVideoDetails ? videoProgresses[watchingVideoDetails.id] === 100 : false}
                upNextBadgeDetails={upNextBadgeDetails}
                eventPopupDetails={eventPopupDetails}
                onClose={() => {
                    console.log( watchingVideoDetails )

                    if ( lastSavedVideoProgress.current[watchingVideoDetails.id] < 90 && !watchingVideoDetails.expired )
                        setVideoPlayerCloseConfirmationDialogOpen( true )
                    else
                        closeVideoPlayer()
                }}
                onTakeQuiz={takeClassQuiz}
                onQuizPopoverClose={closeQuizPopover}
                onRateChange={handleRateChange}
                onTimelineEvent={handleTimelineEvent}
                onEnded={handleVideoCompleted}
                onTimeUpdate={handleVideoPlayerTimeUpdate}
                canRequestExtension={requestsEnabled}
                extensionRequest={timeExtensionRequest}
                requestingExtension={requestingExtension}
                onRequestExtension={requestAccessTimeExtension}
                progressSyncFailing={progressSyncFailing}
            />

            <VideoPlayerCloseConfirmationDialog
                open={videoPlayerCloseConfirmationDialogOpen}
                onClose={() => setVideoPlayerCloseConfirmationDialogOpen( false )}
                onTakeQuiz={takeClassQuiz}
            />

            <AccessExtensionRequestDialog
                open={requestInitializerWindowOpen}
                onClose={() => setRequestInitializerWindowOpen( false )}
                onSubmit={requestAccessTimeExtension}
                submitting={requestingExtension}
            />

            {!meetingFetchError && <BreadcrumbList items={[
                {
                    label: meeting ? `${getTransformedMeetingType( meeting?.meeting_type, { forDisplay: true, plural: true } )}` : <CircularProgress size={14} />,
                    link: "/student/hybrid-classes",
                },
                meeting?.title || <CircularProgress size={14} />
            ]} />}

            {/* Main Content */}
            {!loading && meeting && !meetingFetchError && <Box borderRadius={layout.borderRadius} bgcolor={palette.background.paper}>
                <Box display="flex" gap="10px" alignItems="center">
                    <Tooltip title="Back to hybrid classes">
                        <IconButton sx={{ border: `2px solid ${palette.borderColor}` }} size='small' onClick={() => navigate( `/student/hybrid-classes` )}>
                            {Icons.default.ArrowBack}
                        </IconButton>
                    </Tooltip>
                    <Box>
                        <Box display="flex" gap="5px">
                            <CustomChip variant='filled' color={meeting.can_join ? "success" : meeting.ended ? "error" : !meeting.started ? "secondary" : ""} value={meeting.can_join ? "Active" : meeting.ended ? "Ended" : !meeting.started ? "Not yet started" : ""} />
                            <CustomChip value={meeting.meeting_type} tooltipTitle='Session Type' textStyles={{ textTransform: "uppercase" }} />
                        </Box>
                        <Typography marginTop="10px" fontSize="18px" fontWeight="700" component="h1" variant="h4">{meeting?.title}</Typography>
                    </Box>
                </Box>

                <Box justifyContent="space-between" display="flex" gap="10px" alignItems="center" flexWrap="wrap" marginY="20px">
                    <Box display="flex" alignItems="center" gap="10px">


                        {['class', 'hybrid-class'].includes( meeting.meeting_type ) && QuizStore.getClassQuizzes[meeting.time_table_id]?.length > 0 && QuizStore.getClassQuizzes[meeting.time_table_id]?.map( quiz => <Button size='small' key={quiz.id} onClick={() => attemptQuiz( quiz )} variant='outlined' endIcon={Icons.default.QuizIcon} disableElevation>
                            Take quiz
                        </Button> )}
                    </Box>
                </Box>

                <Box marginTop="10px" display="flex" gap="10px" alignItems="center" flexWrap='wrap'>
                    <Typography padding="5px 10px" bgcolor={palette.contentBg} borderRadius="5px" border={border[1]} fontSize="12px">{formatDate( meeting?.starts_at, { format: "MMM, D hh:mm A" } )}</Typography>
                    <Typography>to</Typography>
                    <Typography padding="5px 10px" bgcolor={palette.contentBg} borderRadius="5px" border={border[1]} fontSize="12px">{formatDate( meeting?.ends_at, { format: "MMM, D hh:mm A" } )}</Typography>
                </Box>

                <Box marginTop="20px" display="flex" flexDirection="column" borderRadius="5px" >

                    {!timeExtensionRequest && canRequestAccessTimeExtension && <Box marginBottom="20px" padding="10px" borderRadius="10px" bgcolor={palette.contentBg} border={border[1]} >
                        <Typography gutterBottom fontWeight="700" variant='h1' fontSize="16px">Request Time Extension</Typography>
                        <Typography marginBottom="20px" fontSize="14px">If you encountered technical difficulties and were unable to finish the video, please request a time extension to ensure your attendance is recorded.</Typography>
                        <Box display="flex" gap="10px" >
                            <Button onClick={() => setRequestInitializerWindowOpen( true )} disabled={requestingExtension} variant='contained' color='customThemeColor' disableElevation endIcon={requestingExtension ? <CircularProgress size={14} /> : Icons.default.Clock}  >Request Time Extension</Button>
                        </Box>
                    </Box>}

                    {!videosFetchError && !loadingRecordings && meetingRecordings.length > 0 && <Box>
                        <Box display="flex" gap="10px" flexDirection="column" flexWrap='wrap'>
                            {meetingRecordings.map( ( video, index ) => {
                                return (
                                    <HybridClassVideo loadingVideo={fetchingVideoLink} videoProgresses={videoProgresses} key={video.id} playVideo={playVideo} video={video} />
                                )
                            } )}
                        </Box>
                    </Box>}

                    {!videosFetchError && !loadingRecordings && meetingRecordings.length === 0 && <Box display="flex" flexDirection="column" gap="20px" alignItems="center" bgcolor={palette.contentBg} padding="20px" borderRadius={layout.borderRadius} >
                        <Box component="img" width="200px" src={noRecordingsImg} />
                        <Typography variant='subtitle2' component="p" textAlign="center">No videos are available for watching.</Typography>
                    </Box>}
                    {videosFetchError && <TryAgainMessageBlock parent='component' getData={fetchMeetingRecordings} loading={loadingRecordings} code={videosFetchError?.code} err={videosFetchError?.message} />}
                </Box>
            </Box>}

            {loading && !meetingFetchError && <DataLoadingSpinner waitingMessage="Loading meeting details..." />}
            {!loading && meetingFetchError && <TryAgainMessageBlock getData={fetchMeeting} code={meetingFetchError?.code} loading={refreshing} err={meetingFetchError?.message} />}

        </Box >
    )
} )

export default HybridClassDetailsForStudent


/* {meeting.can_join && <Link className='default-link' to={`${window.origin}/hybrid-class/${meeting.id}`}>
                            <Button size='small' sx={{ background: `linear-gradient(125.39deg, #139CFF -32.54%, #C54097 110.4%)`, ":disabled": { color: palette.grey[500], background: palette.grey[700] } }} variant='contained' endIcon={Icons.default.Login} disableElevation>
                                Stream Class
                            </Button>
                        </Link>} */
/* {!meeting.can_join && <Tooltip title={!meeting.started ? "Session Not Started" : meeting.ended ? "Session Ended" : ""}>
                            <Box component="span">
                                <Button size='small' disabled={true} sx={{ background: `linear-gradient(125.39deg, #139CFF -32.54%, #C54097 110.4%)`, ":disabled": { color: palette.grey[500], background: palette.grey[700] } }} variant='contained' endIcon={Icons.default.OpenInNewIcon} disableElevation>
                                    Stream Class
                                </Button>
                            </Box>
                        </Tooltip>} 
                        
                        {timeExtensionRequest && timeExtensionRequest.status !== "accepted" && requestsEnabled && <Box marginBottom="20px" display="flex" alignItems="flex-start" gap="10px" padding="10px" borderRadius="10px" bgcolor={palette.contentBg} border={border[1]} >
                        <Typography marginTop="-2px" color='secondary' fontSize="20px">{Icons.default.Info}</Typography>
                        <Box>
                            <Typography gutterBottom fontWeight="700" color='secondary' variant='h1' fontSize="16px">Time Extension Request Sent</Typography>
                            <Typography fontSize="12px">Your request for a time extension was submitted at {formatDate( timeExtensionRequest?.requested_at, { format: "hh:mm A" } )}. Please wait for the faculty to review and approve your request.</Typography>
                        </Box>
                    </Box>}
                        */